import { type AdminModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type AdminsSortState } from '../../lib/admins-query-model'
import {
  $adminsTablePage,
  $adminsTablePageSize,
  $adminsTableSort,
  adminsTablePageChanged,
  adminsTablePageReset,
  adminsTablePageSizeChanged,
  adminsTableQuery,
  adminsTableSortChanged,
  adminsTableSortReset,
} from '../../model/admins-table.model'
import { useAdminsTableColumns } from './useAdminsTableColumns'

interface AdminsTableProps {
  onEdit: (adminId: string) => void
}
export const AdminsTable = ({ onEdit }: AdminsTableProps) => {
  const { data, pending } = useUnit(adminsTableQuery)

  const sort = useUnit($adminsTableSort)
  const changeSort = useUnit(adminsTableSortChanged)
  const resetSort = useUnit(adminsTableSortReset)

  const page = useUnit($adminsTablePage)
  const changePage = useUnit(adminsTablePageChanged)
  const resetPage = useUnit(adminsTablePageReset)
  const pageSize = useUnit($adminsTablePageSize)
  const changePageSize = useUnit(adminsTablePageSizeChanged)

  const tableColumns: TableProps<AdminModel>['columns'] = useAdminsTableColumns(
    { onEdit, sort },
  )

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<AdminModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as AdminsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Администраторов на странице' },
        }}
      />
    </TableWrapper>
  )
}
